/**
 * ストレージアクセスの一元化ユーティリティ
 * - Cookie / localStorage の読み書きをここに集約する
 * - 認証関連のキー名は定数で管理する
 */

// localStorageのキー定数
export const STORAGE_KEYS = {
  TOKEN: 'token',
  ORGANIZATION_ID: 'organizationId',
  USER_ID: 'userId',
  ROLE_ID: 'roleId',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

// Cookieのキー定数（バックエンドが設定する名前に合わせる）
export const COOKIE_KEYS = {
  TOKEN: 'token',
  ORGANIZATION_ID: 'organizationID',
  USER_ID: 'userID',
} as const;

export type CookieKey = (typeof COOKIE_KEYS)[keyof typeof COOKIE_KEYS];

/** SSR環境かどうか */
function isServer(): boolean {
  return typeof window === 'undefined';
}

// ---------- Cookie ----------

/** Cookieを取得する */
export function getCookie(name: string): string | null {
  if (isServer()) return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

/** Cookieを設定する */
export function setCookie(name: string, value: string, maxAgeSeconds?: number): void {
  if (isServer()) return;
  const maxAge = maxAgeSeconds !== undefined ? `; max-age=${maxAgeSeconds}` : '';
  document.cookie = `${name}=${value}; path=/${maxAge}`;
}

/** Cookieを削除する */
export function deleteCookie(name: string): void {
  if (isServer()) return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// ---------- localStorage（文字列） ----------

/** localStorageから文字列を取得する */
export function getStoredString(key: string): string | null {
  if (isServer()) return null;
  return localStorage.getItem(key);
}

/** localStorageに文字列を保存する */
export function setStoredString(key: string, value: string): void {
  if (isServer()) return;
  localStorage.setItem(key, value);
}

/** localStorageから値を削除する */
export function removeStoredItem(key: string): void {
  if (isServer()) return;
  localStorage.removeItem(key);
}

// ---------- localStorage（数値: JSON形式） ----------

/** localStorageからJSON形式で保存された数値を取得する */
export function getStoredNumber(key: string): number | null {
  if (isServer()) return null;
  const raw = localStorage.getItem(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'number' ? parsed : null;
  } catch (error) {
    console.error(`Failed to parse '${key}' from localStorage`, error);
    return null;
  }
}

/** localStorageに数値をJSON形式で保存する */
export function setStoredNumber(key: string, value: number): void {
  if (isServer()) return;
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * localStorageからID値を文字列として読み込む（HTTPヘッダ用）
 * JSON.stringifyされた値・生文字列のどちらにも対応する
 */
export function readStoredValue(key: string): string | null {
  if (isServer()) return null;
  const raw = localStorage.getItem(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed === null ? null : String(parsed);
  } catch {
    return raw;
  }
}
